import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { fmt, fdate } from "@/lib/api";
import { portalApi, usePortalLoad, downloadWith } from "@/lib/portalApi";
import { PageHeader, Section, StatusBadge, StatCard, Field, NativeSelect } from "@/components/common";
import { MilestoneSchedule } from "@/components/MilestoneSchedule";

const BANK = [["bank_name", "Bank"], ["account_name", "Account name"], ["account_number", "Account number"], ["bank_branch", "Branch"], ["swift_code", "SWIFT"]];
const MOMO = [["momo_network", "Network"], ["momo_number", "Number"], ["momo_name", "Registered name"]];
const METHODS = [["bank_transfer", "Bank transfer"], ["momo", "Mobile money"], ["cheque", "Cheque"], ["cash", "Cash"]].map(([value, label]) => ({ value, label }));

function Details({ title, rows, pd, testId }) {
  const shown = rows.filter(([k]) => pd[k]);
  if (!shown.length) return null;
  return (
    <div data-testid={testId}><div className="po-label mb-1">{title}</div>
      <dl className="space-y-1">{shown.map(([k, l]) => <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{l}</dt><dd className="font-medium text-slate-900" data-testid={`${testId}-${k}`}>{pd[k]}</dd></div>)}</dl>
    </div>
  );
}

function PayPanel({ d, bal, onClaim }) {
  const inv = d.invoice;
  const pd = d.payment_details || {};
  const claim = inv.payment_claim;
  if (inv.status === "cancelled") return <div className="po-card p-6 text-center text-slate-600" data-testid="portal-cancelled-message">This invoice was cancelled. Nothing is due.</div>;
  if (inv.status === "paid") return <div className="po-card p-6 text-center text-emerald-700" data-testid="portal-paid-message">This invoice is fully paid. Thank you!</div>;
  return (
    <div className="po-card space-y-4 p-6 text-sm" data-testid="portal-payment-instructions">
      <div className="text-base font-semibold text-slate-900">How to pay {fmt(bal, inv.currency)}</div>
      <Details title="Bank transfer" rows={BANK} pd={pd} testId="pay-bank" />
      <Details title="Mobile money" rows={MOMO} pd={pd} testId="pay-momo" />
      {pd.instructions && <p className="text-slate-600">{pd.instructions}</p>}
      {!BANK.concat(MOMO).some(([k]) => pd[k]) && <p className="text-slate-600">Contact {d.tenant_name} for payment details.</p>}
      <p className="text-xs text-slate-500">Always quote <span className="font-mono font-semibold">{inv.number}</span> as your payment reference.</p>
      {claim?.status === "pending" ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800" data-testid="portal-claim-pending">Payment claimed, awaiting confirmation from {d.tenant_name} ({fmt(claim.amount, inv.currency)} on {fdate(claim.claimed_at)}).</div>
      ) : (
        <>
          {claim?.status === "dismissed" && <p className="text-xs text-rose-700" data-testid="portal-claim-dismissed">Your last payment claim could not be matched{claim.resolution_note && `: ${claim.resolution_note}`}.</p>}
          <Button data-testid="portal-mark-paid-button" onClick={onClaim} className="w-full bg-[#13294B] hover:bg-[#1c3a66]"><BadgeCheck className="mr-1 h-4 w-4" />Mark as paid (pending confirmation)</Button>
        </>
      )}
    </div>
  );
}

export default function PortalInvoice() {
  const { id } = useParams();
  const [d, reload] = usePortalLoad(`/invoices/${id}`);
  const [claim, setClaim] = useState(null);
  if (!d) return null;
  const inv = d.invoice;
  const bal = inv.status === "cancelled" ? 0 : Math.round((inv.total - inv.amount_paid - (inv.amount_credited || 0)) * 100) / 100;
  const submit = async () => {
    const r = await act(() => portalApi.post(`/invoices/${id}/claim-payment`, { ...claim, amount: Number(claim.amount) || null }), "Thanks! We've let them know you've paid.");
    if (r) { setClaim(null); reload(); }
  };
  return (
    <>
      <Link to="/portal/invoices" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" />All invoices</Link>
      <PageHeader title={inv.number} subtitle={`Issued ${fdate(inv.created_at)} · due ${fdate(inv.due_date)}`}>
        <StatusBadge status={inv.status} testId="portal-invoice-status" className="self-center" />
        {inv.payment_claim?.status === "pending" && <StatusBadge status="pending" label="Payment claimed — awaiting confirmation" testId="portal-claim-badge" className="self-center" />}
        <Button variant="outline" data-testid="portal-invoice-pdf" onClick={() => downloadWith(portalApi, `/invoices/${id}/pdf`, `${inv.number}.pdf`, true)}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard testId="portal-invoice-total" label="Invoice total" value={fmt(inv.total, inv.currency)} />
        <StatCard testId="portal-invoice-paid" label="Paid" value={fmt(inv.amount_paid, inv.currency)} accent />
        <StatCard testId="portal-invoice-balance" label="Balance" value={fmt(bal, inv.currency)} />
      </div>
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <Section title="Items">
            <ul className="divide-y divide-slate-100">{inv.line_items.map((l, i) => (
              <li key={i} className="flex justify-between gap-3 px-5 py-3 text-sm"><span>{l.description} <span className="text-slate-400">× {l.quantity}</span></span><span className="tabular-nums">{fmt(l.line_total, inv.currency)}</span></li>
            ))}</ul>
          </Section>
          {d.rules.schedule && <Section title="Payment schedule"><MilestoneSchedule schedule={d.rules.schedule} currency={inv.currency} /></Section>}
          <Section title="Receipts">
            <ul className="divide-y divide-slate-100" data-testid="portal-receipts">
              {d.receipts.length === 0 && <li className="px-5 py-4 text-sm text-slate-400">No payments yet.</li>}
              {d.receipts.map((r) => <li key={r.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm"><span className="font-mono text-xs">{r.number} · {fdate(r.date || r.created_at)} · {r.method_label || r.method}</span>
                <span className="flex items-center gap-2"><span className="tabular-nums">{fmt(r.amount, r.currency)}</span>
                  <Button size="sm" variant="ghost" data-testid={`portal-receipt-pdf-${r.number}`} onClick={() => downloadWith(portalApi, `/receipts/${r.id}/pdf`, `${r.number}.pdf`, true)}><FileDown className="h-4 w-4" /></Button></span></li>)}
            </ul>
          </Section>
        </div>
        <div className="lg:col-span-2"><PayPanel d={d} bal={bal} onClaim={() => setClaim({ method: "bank_transfer", reference: "", amount: bal, note: "" })} /></div>
      </div>
      <Dialog open={!!claim} onOpenChange={() => setClaim(null)}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader><DialogTitle>Tell {d.tenant_name} you've paid</DialogTitle>
            <DialogDescription>Your invoice stays unpaid until they confirm the money has arrived.</DialogDescription></DialogHeader>
          {claim && <div className="space-y-3">
            <Field label="How did you pay?"><NativeSelect testId="claim-method-select" value={claim.method} onChange={(v) => setClaim({ ...claim, method: v })} options={METHODS} /></Field>
            <Field label="Amount"><Input type="number" step="0.01" data-testid="claim-amount-input" value={claim.amount} onChange={(e) => setClaim({ ...claim, amount: e.target.value })} /></Field>
            <Field label="Transaction reference"><Input data-testid="claim-reference-input" value={claim.reference} onChange={(e) => setClaim({ ...claim, reference: e.target.value })} placeholder="e.g. MoMo transaction ID" /></Field>
          </div>}
          <DialogFooter><Button data-testid="claim-submit-button" onClick={submit}>Send payment claim</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
